<!--
 * @Author: Vben
 * @Description: logo component
-->
<script lang="ts" setup>
    import { computed, unref } from 'vue'
    import { useGlobSetting } from '/@/hooks/setting'
    import { useGo } from '/@/hooks/web/usePage'
    import { useMenuSetting } from '/@/hooks/setting/useMenuSetting'
    import { useDesign } from '/@/hooks/web/useDesign'
    import { PageEnum } from '/@/enums/pageEnum'
    import { useUserStore } from '/@/store/modules/user'
    import { getEnvText } from '/@/utils/env'

    import logo from '@/assets/images/logo.jpg'

    const props = defineProps({
        appTitle: { type: String, default: '' },
        appLogo: { type: String, default: '' },
        imgStyle: { type: String, default: '' },
        /**
         * The theme of the current parent component
         */
        theme: { type: String, validator: (v: string) => ['light', 'dark'].includes(v) },
        /**
         * Whether to show title
         */
        showTitle: { type: Boolean, default: true },
        /**
         * The title is also displayed when the menu is collapsed
         */
        alwaysShowTitle: { type: Boolean },
        /**
         *  Whether to show logo
         */
        showLogo: { type: Boolean, default: true },
    })

    const { prefixCls } = useDesign('app-logo')
    const { getCollapsedShowTitle } = useMenuSetting()
    const userStore = useUserStore()
    const { title } = useGlobSetting()
    const go = useGo()

    const getAppLogoClass = computed(() => [
        prefixCls,
        props.theme,
        { 'collapsed-show-title': unref(getCollapsedShowTitle) },
    ])

    const getTitleClass = computed(() => [
        `${prefixCls}__title`,
        {
            'xs:opacity-0': !props.alwaysShowTitle,
        },
    ])

    const version = `${getEnvText()}@${$app.version} -> ${$app.lastBuildTime}`

    function goHome() {
        go(userStore.getUserInfo.homePath || PageEnum.BASE_HOME)
    }
</script>

<template>
    <div class="anticon" :class="getAppLogoClass" @click="goHome" :title="version">
        <img :src="appLogo || logo" v-if="showLogo" :style="imgStyle" />

        <div class="ml-2 truncate md:opacity-100" :class="getTitleClass" v-if="showTitle">
            {{ appTitle || title }}<span style="font-size: 20px; margin-left: 5px">V2.0</span>
        </div>
    </div>
</template>

<style lang="less" scoped>
    @prefix-cls: ~'@{namespace}-app-logo';

    .@{prefix-cls} {
        display: flex;
        align-items: center;
        padding-left: 7px;
        cursor: pointer;
        transition: all 0.2s ease;

        // &.light {
        //     border-bottom: 1px solid @border-color-base;
        // }

        &.collapsed-show-title {
            padding-left: 20px;
        }

        &.light &__title {
            color: @primary-color;
        }

        &.dark &__title {
            color: @white;
        }

        &__title {
            font-family: SimHei;
            font-size: 28px !important;
            letter-spacing: 1px;
            font-weight: 700;
            transition: all 0.5s;
            line-height: normal;
        }
    }
</style>
